import type {
  AwsCredentialIdentity,
  AwsCredentialIdentityProvider,
} from "@aws-sdk/types";

import { LoggerService } from "@/shared/services";
import { DatasetsCredentialsResponse } from "@/types";

import { getFileUploadCredentials } from "./getFileUploadCredentials";

/**
 * Re-fetch once the credentials are within this long of expiring, rather than waiting for them to
 * lapse. A part already in flight when they expire fails, so the window has to cover the longest
 * request likely to be outstanding.
 */
const REFRESH_WINDOW_MS = 5 * 60 * 1000;

/**
 * Builds the credential provider an upload's `S3Client` resolves its credentials through.
 *
 * Returned as a *function* rather than a credentials object on purpose. The AWS SDK v3 only
 * re-resolves credentials that came from a provider function, and only when the identity it last
 * resolved carries an `expiration` to compare against; a plain object is treated as fixed for the
 * life of the client. The service issues these credentials with a one-hour lifetime, so an upload
 * running longer than that used to fail partway through with no way to recover.
 *
 * Seeded with the credentials the caller already fetched, and shared across every file in a batch,
 * so the common case costs no extra request: a batch of 500 files fetches once and refreshes only
 * if it is still running an hour later.
 */
export function createUploadCredentialsProvider(
  initialCredentials: DatasetsCredentialsResponse,
  datasetId: string,
  orgId: string,
  transactionId: string,
): AwsCredentialIdentityProvider {
  let current = toIdentity(initialCredentials);
  let inFlightRefresh: Promise<AwsCredentialIdentity> | null = null;

  return async (): Promise<AwsCredentialIdentity> => {
    if (!isNearingExpiry(current)) {
      return current;
    }

    // Parts upload concurrently, so several requests can find the credentials stale at once.
    // Share a single refresh between them instead of stampeding the credentials endpoint.
    inFlightRefresh ??= fetchIdentity(datasetId, orgId, transactionId)
      .then((identity) => {
        current = identity;
        return identity;
      })
      .finally(() => {
        inFlightRefresh = null;
      });

    return inFlightRefresh;
  };
}

async function fetchIdentity(
  datasetId: string,
  orgId: string,
  transactionId: string,
): Promise<AwsCredentialIdentity> {
  const { response, error } = await getFileUploadCredentials(
    datasetId,
    orgId,
    transactionId,
  );

  if (error || !response?.data) {
    LoggerService.error("Error getting credentials for dataset upload", error);
    throw error ?? new Error("Error getting credentials for dataset upload");
  }

  return toIdentity(response);
}

function toIdentity(
  credentials: DatasetsCredentialsResponse,
): AwsCredentialIdentity {
  const { access_key_id, secret_access_key, session_token, expiration } =
    credentials.data;

  return {
    accessKeyId: access_key_id,
    secretAccessKey: secret_access_key,
    sessionToken: session_token,
    expiration: new Date(expiration),
  };
}

function isNearingExpiry(identity: AwsCredentialIdentity): boolean {
  const expiresAt = identity.expiration?.getTime();

  if (expiresAt === undefined || Number.isNaN(expiresAt)) {
    // Without a usable expiry there is nothing to schedule a refresh against. Hold the credentials
    // we have, which is how this behaved before refreshing existed, rather than re-fetching on
    // every signed request.
    LoggerService.error(
      "Upload credentials carry no usable expiration; refresh is disabled for this upload",
    );
    return false;
  }

  return expiresAt - Date.now() <= REFRESH_WINDOW_MS;
}
